import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from './config';
import { fetchStores } from './api/stores';
import CompanySettings from './components/admin/CompanySettings';
import StoresManager from './components/admin/StoresManager';
import PaymentTypesManager from './components/admin/PaymentTypesManager';
import TaxRatesManager from './components/admin/TaxRatesManager';
import VoidReasonsManager from './components/admin/VoidReasonsManager';
import DocumentsManager from './components/admin/DocumentsManager';
import FormsManager from './components/admin/FormsManager';
import ProductsManager from './components/admin/ProductsManager';
import StockManager from './components/admin/StockManager';
import ReportingManager from './components/admin/ReportingManager';
import CustomersManager from './components/admin/CustomersManager';
import SuppliersManager from './components/admin/SuppliersManager';
import UsersManager from './components/admin/UsersManager';
import EmployeesManager from './components/admin/EmployeesManager';
import AnnouncementsManager from './components/admin/AnnouncementsManager';
import AdminSidebar from './components/admin/AdminSidebar';
import AdminDashboard from './components/admin/AdminDashboard';

function getAuthConfig(authToken) {
  return {
    headers: { Authorization: `Bearer ${authToken}` }
  };
}

export default function AdminPanel({
  authToken,
  onAuthExpired,
  onLogout,
  onBack,
  backendUrl = API_BASE_URL
}) {
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [, setIsLoadingStores] = useState(true);
  const [adminTab, setAdminTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [customersSubTab, setCustomersSubTab] = useState('customers');

  const handleUnauthorized = useCallback((err) => {
    if (err?.response?.status === 401) {
      if (onAuthExpired) onAuthExpired();
      return true;
    }
    return false;
  }, [onAuthExpired]);

  const loadStores = useCallback(async () => {
    try {
      setIsLoadingStores(true);
      const list = await fetchStores();
      const arr = Array.isArray(list) ? list : [];
      setStores(arr);
      setSelectedStoreId((prev) => {
        if (prev !== '') return prev;
        return arr.length > 0 ? (arr[0]._id || arr[0].id || '') : '';
      });
    } catch (err) {
      console.error('Failed to load stores:', err);
      setStores([]);
    } finally {
      setIsLoadingStores(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const authConfig = useMemo(() => getAuthConfig(authToken), [authToken]);

  return (
    <div className="min-h-screen flex bg-gray-100">
      <AdminSidebar
        activeTab={adminTab}
        onTab={setAdminTab}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed((c) => !c)}
        onLogout={onLogout || onBack}
      />
      <main className="flex-1 overflow-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-sm text-indigo-600 hover:text-indigo-800 underline">
            ← Back to Store
          </button>
        </div>

        {/* Dashboard */}
        {adminTab === 'dashboard' && (
          <AdminDashboard
            authConfig={authConfig}
            backendUrl={backendUrl}
            onUnauthorized={handleUnauthorized}
          />
        )}

        {/* Reporting */}
        {adminTab === 'reporting' && (
          <ReportingManager
            authConfig={authConfig}
            backendUrl={backendUrl}
            onUnauthorized={handleUnauthorized}
            stores={stores}
          />
        )}

        {/* Stores, Company, Payment types, Tax rates, Void reasons */}
        {adminTab === 'stores' && (
          <section className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-semibold mb-4">Stores</h3>
            <StoresManager authConfig={authConfig} onUnauthorized={handleUnauthorized} onStoresChange={loadStores} />
          </section>
        )}
        {adminTab === 'company' && (
          <section className="overflow-hidden rounded-lg shadow">
            <CompanySettings authConfig={authConfig} onUnauthorized={handleUnauthorized} />
          </section>
        )}
        {adminTab === 'payment-types' && (
          <section className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-semibold mb-4">Payment types</h3>
            <PaymentTypesManager authConfig={authConfig} onUnauthorized={handleUnauthorized} />
          </section>
        )}
        {adminTab === 'tax-rates' && (
          <section className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-semibold mb-4">% Tax rates</h3>
            <TaxRatesManager authConfig={authConfig} onUnauthorized={handleUnauthorized} />
          </section>
        )}
        {adminTab === 'void-reasons' && (
          <section className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-semibold mb-4">Void reasons</h3>
            <VoidReasonsManager authConfig={authConfig} onUnauthorized={handleUnauthorized} />
          </section>
        )}

        {adminTab === 'documents' && (
        <section className="bg-white p-4 rounded shadow mb-8">
          <h3 className="text-xl font-semibold mb-4">Sales &amp; stock documents</h3>
          <DocumentsManager
            authConfig={authConfig}
            backendUrl={backendUrl}
            onUnauthorized={handleUnauthorized}
          />
        </section>
        )}

        {adminTab === 'stock' && (
          <StockManager
            authConfig={authConfig}
            backendUrl={backendUrl}
            onUnauthorized={handleUnauthorized}
            stores={stores}
            selectedStoreId={selectedStoreId}
            onStoreChange={setSelectedStoreId}
          />
        )}

        {adminTab === 'customers' && (
        <section className="bg-white p-4 rounded shadow mb-8">
          <h3 className="text-xl font-semibold mb-4">Customers & Suppliers</h3>
          <div className="flex gap-2 mb-4">
            <button type="button" onClick={() => setCustomersSubTab('customers')} className={`px-3 py-1.5 rounded text-sm ${customersSubTab === 'customers' ? 'bg-indigo-600 text-white' : 'bg-gray-100'}`}>Customers</button>
            <button type="button" onClick={() => setCustomersSubTab('suppliers')} className={`px-3 py-1.5 rounded text-sm ${customersSubTab === 'suppliers' ? 'bg-indigo-600 text-white' : 'bg-gray-100'}`}>Suppliers</button>
          </div>
          {customersSubTab === 'customers' && <CustomersManager authConfig={authConfig} onUnauthorized={handleUnauthorized} />}
          {customersSubTab === 'suppliers' && <SuppliersManager authConfig={authConfig} onUnauthorized={handleUnauthorized} />}
        </section>
        )}

        {adminTab === 'users' && (
        <section className="bg-white p-4 rounded shadow mb-8">
          <h3 className="text-xl font-semibold mb-4">Users & Access</h3>
          <UsersManager authConfig={authConfig} onUnauthorized={handleUnauthorized} />
        </section>
        )}

        {adminTab === 'employees' && (
        <section className="bg-white p-4 rounded shadow mb-8">
          <h3 className="text-xl font-semibold mb-4">Employees (Attendance)</h3>
          <EmployeesManager authConfig={authConfig} authToken={authToken} backendUrl={backendUrl} onUnauthorized={handleUnauthorized} />
        </section>
        )}

        {adminTab === 'forms' && (
        <section className="bg-white p-4 rounded shadow mb-8">
          <h3 className="text-xl font-semibold mb-4">Forms</h3>
          <FormsManager
            authConfig={authConfig}
            onUnauthorized={handleUnauthorized}
          />
        </section>
        )}

        {adminTab === 'announcements' && (
        <section className="bg-white p-4 rounded shadow mb-8">
          <h3 className="text-xl font-semibold mb-4">Calendar Announcements</h3>
          <AnnouncementsManager authConfig={authConfig} onUnauthorized={handleUnauthorized} stores={stores} />
        </section>
        )}

        {adminTab === 'products' && (
          <ProductsManager
            authConfig={authConfig}
            backendUrl={backendUrl}
            onUnauthorized={handleUnauthorized}
            stores={stores}
            selectedStoreId={selectedStoreId}
            onStoreChange={setSelectedStoreId}
          />
        )}
      </main>
    </div>
  );
}
